import api from './client';

export type TransactionType =
  | 'Listing Fee'
  | 'Commission'
  | 'App Fee'
  | 'Subscription'
  | 'Payout';

export type TransactionStatus = 'paid' | 'pending' | 'refunded' | 'failed';

export interface RevenueKpis {
  totalRevenue: number;
  monthRevenue: number;
  weekRevenue: number;
  /** Paid revenue in the selected year. */
  yearRevenue: number;
  pendingPayouts: number;
  pendingPayoutAgents: number;
}

export interface RevenueBreakdownRow {
  month: string;
  key: string;
  year: number;
  'Listing Fee': number;
  Commission: number;
  'App Fee': number;
  Subscription: number;
  total: number;
  /** Number of payments per type that month. */
  counts?: { 'Listing Fee': number; Commission: number; 'App Fee': number; Subscription: number };
}

/** Paid revenue in a custom date range (only when from/to were sent). */
export interface RevenuePeriod {
  from: string | null;
  to: string | null;
  total: number;
  byType: { Commission: number; 'App Fee': number; Subscription: number };
}

export interface RevenueSummary {
  kpis: RevenueKpis;
  /** Year charted in `breakdown` (January–December). */
  year: number;
  /** Years that have revenue, plus this year — newest first. */
  availableYears: number[];
  breakdown: RevenueBreakdownRow[];
  period?: RevenuePeriod;
}

export interface Transaction {
  id: number;
  transactionRef: string;
  type: TransactionType;
  amount: string | number;
  currency: string;
  status: TransactionStatus;
  counterpartyId: number | null;
  counterpartyName: string | null;
  reference: string | null;
  referenceType: string | null;
  referenceId: number | null;
  description: string | null;
  occurredAt: string;
  createdAt?: string;
  updatedAt?: string;
  counterparty?: { id: number; name: string; email: string } | null;
}

export interface TransactionListParams {
  type?: TransactionType;
  status?: TransactionStatus;
  from?: string;
  to?: string;
  /** Customer/merchant name or transaction ID ("TXN-0105", "105"). */
  q?: string;
  page?: number;
  limit?: number;
}

export interface TransactionListResponse {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  transactions: Transaction[];
}

export interface PendingPayout {
  agentId: number | null;
  agentName: string;
  amount: number;
  count: number;
}

/** GET /api/revenue/summary */
export const getRevenueSummary = async (
  range: { from?: string; to?: string; year?: number } = {}
): Promise<RevenueSummary> => {
  const params: Record<string, string> = {};
  if (range.from) params.from = range.from;
  if (range.to) params.to = range.to;
  if (range.year) params.year = String(range.year);
  const res = await api.get<{ status: number } & RevenueSummary>(
    '/revenue/summary',
    { params }
  );
  return {
    kpis: res.data.kpis,
    year: res.data.year,
    availableYears: res.data.availableYears,
    breakdown: res.data.breakdown,
    period: res.data.period,
  };
};

/** GET /api/revenue/transactions */
export const listTransactions = async (
  params: TransactionListParams = {}
): Promise<TransactionListResponse> => {
  const query: Record<string, string> = {};
  if (params.type) query.type = params.type;
  if (params.status) query.status = params.status;
  if (params.from) query.from = params.from;
  if (params.to) query.to = params.to;
  if (params.q) query.q = params.q;
  if (params.page) query.page = String(params.page);
  if (params.limit) query.limit = String(params.limit);

  const res = await api.get<
    { status: number } & TransactionListResponse
  >('/revenue/transactions', { params: query });
  return {
    total: res.data.total,
    page: res.data.page,
    limit: res.data.limit,
    totalPages: res.data.totalPages,
    transactions: res.data.transactions,
  };
};

/** GET /api/revenue/payouts */
export const listPendingPayouts = async (): Promise<PendingPayout[]> => {
  const res = await api.get<{ status: number; payouts: PendingPayout[] }>(
    '/revenue/payouts'
  );
  return res.data.payouts;
};

/** POST /api/revenue/payouts/:agentId/process */
export const processAgentPayout = async (
  agentId: number
): Promise<{ amount: number; settledCount: number }> => {
  const res = await api.post<{
    status: number;
    amount: number;
    settledCount: number;
  }>(`/revenue/payouts/${agentId}/process`);
  return { amount: res.data.amount, settledCount: res.data.settledCount };
};
